import { API, Auth } from "aws-amplify"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"

import { brandsFromApi, brandFromApi, brandToApi } from "./brandMapper"

const CORE = "CORE"

// ---------------------------------------------------------------------------
// Fetch primitives
//
// The Cognito token is attached explicitly, as in Taxes/hooks/useTaxes.js:
// the Amplify config in src/index.js registers CORE without a custom_header,
// so nothing injects it for us.
// ---------------------------------------------------------------------------

const authPayload = async body => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
  }
  if (body !== undefined) payload.body = body
  return payload
}

const fetchBrands = async organizationId => {
  if (!organizationId) return []
  const res = await API.get(
    CORE,
    `/brands/${organizationId}`,
    await authPayload(),
  )
  return brandsFromApi(res)
}

const fetchCreateBrand = async ({ organizationId, brand }) => {
  const body = { ...brandToApi(brand), organizationId, name: brand.name }
  if (brand.isDefault) body.is_default = true
  const res = await API.post(CORE, "/brands/", await authPayload(body))
  return brandFromApi(res)
}

const fetchUpdateBrand = async ({ organizationId, brandId, brand }) => {
  const url = `/brands/${organizationId}/${encodeURIComponent(brandId)}`
  const res = await API.patch(CORE, url, await authPayload(brandToApi(brand)))
  return brandFromApi(res)
}

const fetchSetDefaultBrand = async ({ organizationId, brandId }) => {
  const url = `/brands/${organizationId}/${encodeURIComponent(brandId)}/default`
  const res = await API.post(CORE, url, await authPayload())
  return brandFromApi(res)
}

const fetchDeleteBrand = async ({ organizationId, brandId }) => {
  const url = `/brands/${organizationId}/${encodeURIComponent(brandId)}`
  return API.del(CORE, url, await authPayload())
}

// ---------------------------------------------------------------------------
// React Query hooks
// ---------------------------------------------------------------------------

export const brandsQueryKey = organizationId => ["brands", organizationId]

export const useBrands = (organizationId, { enabled = true } = {}) => {
  return useQuery({
    queryKey: brandsQueryKey(organizationId),
    queryFn: () => fetchBrands(organizationId),
    enabled: !!organizationId && enabled,
  })
}

const useInvalidateBrands = organizationId => {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries({ queryKey: brandsQueryKey(organizationId) })
}

export const useCreateBrand = organizationId => {
  const invalidate = useInvalidateBrands(organizationId)
  return useMutation({
    mutationFn: brand => fetchCreateBrand({ organizationId, brand }),
    onSuccess: invalidate,
  })
}

export const useUpdateBrand = organizationId => {
  const invalidate = useInvalidateBrands(organizationId)
  return useMutation({
    mutationFn: ({ brandId, brand }) =>
      fetchUpdateBrand({ organizationId, brandId, brand }),
    onSuccess: invalidate,
  })
}

export const useSetDefaultBrand = organizationId => {
  const invalidate = useInvalidateBrands(organizationId)
  return useMutation({
    mutationFn: brandId => fetchSetDefaultBrand({ organizationId, brandId }),
    onSuccess: invalidate,
  })
}

export const useDeleteBrand = organizationId => {
  const invalidate = useInvalidateBrands(organizationId)
  return useMutation({
    mutationFn: brandId => fetchDeleteBrand({ organizationId, brandId }),
    onSuccess: invalidate,
  })
}
